import { Button, Dialog, Flex } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";

interface PromoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending?: boolean;
  /** Override du titre (par défaut `common.promote.title`). */
  title?: string;
  /** Override du message de confirmation (par défaut `common.promote.confirm`). */
  confirm?: string;
  /** Override du libellé du bouton (par défaut `common.promote.action`). */
  action?: string;
  /** Override du libellé annuler (par défaut `common.cancel`). */
  cancel?: string;
  /**
   * Contenu additionnel inséré entre la description et les boutons.
   * Permet d'enrichir le dialog sans le coupler à une logique métier spécifique.
   * Ex : sélecteur de layout pour les cartes météo.
   */
  children?: ReactNode;
}

/**
 * Modale "Mettre en avant" générique — partagée par photos, cartes météo,
 * et toute future ressource. Aucun couplage métier : juste un dialogue
 * confirm/cancel + un callback `onConfirm`.
 *
 * La prop `children` permet d'insérer du contenu contextuel (ex : sélecteur
 * de layout) sans altérer le comportement de base.
 */
export function PromoteDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  title,
  confirm,
  action,
  cancel,
  children,
}: PromoteDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="400px">
        <Dialog.Title>{title ?? t("common.promote.title")}</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb={children ? "3" : "4"}>
          {confirm ?? t("common.promote.confirm")}
        </Dialog.Description>

        {children && (
          <Flex direction="column" gap="2" mb="4">
            {children}
          </Flex>
        )}

        <Flex gap="3" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              {cancel ?? t("common.cancel")}
            </Button>
          </Dialog.Close>
          <Button color="violet" onClick={onConfirm} loading={isPending}>
            {action ?? t("common.promote.action")}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
